import {
  STOCKY_COPY_PROFILES,
  type StockyCopyProfile,
  type StockyCopyResultDomain,
} from "../../api/stocky_copy_jobs";

export type { StockyCopyProfile } from "../../api/stocky_copy_jobs";

export const STOCKY_COPY_PROFILE_LABEL: Record<StockyCopyProfile, string> = {
  api_plus_web_v1: "Stockyコピー（〜8/31）",
  web_only_v1: "Stockyコピー（終了後の読み取り専用期間）",
  api_plus_web_v2: "Stockyコピー（〜8/31）",
  web_only_v2: "Stockyコピー（終了後の読み取り専用期間）",
  api_plus_web_v3: "Stockyコピー（〜8/31）",
  web_only_v3: "Stockyコピー（終了後の読み取り専用期間）",
};

/**
 * Public availability boundary for every renderable web-only profile.
 * Keep this wording shared so entry, receipt, and help surfaces cannot imply
 * that Shopify's post-shutdown read-only UI will remain available indefinitely.
 */
export const STOCKY_POST_SHUTDOWN_READ_ONLY_NOTICE =
  "Shopifyは、Stocky終了後も少なくとも90日間は読み取り専用でアクセスできると案内しています。このコピー経路を利用できるのは、対応するStockyのページ／UIが利用可能で、構造が変更されていない間だけです。90日を超える期間の利用は保証されません。";

export const STOCKY_COPY_TAB_IDS = [
  "api_plus_web_v3",
  "web_only_v3",
  "manual_csv",
] as const;

export type StockyCopyTabId = (typeof STOCKY_COPY_TAB_IDS)[number];

export const STOCKY_COPY_TAB_LABEL: Record<StockyCopyTabId, string> = {
  ...STOCKY_COPY_PROFILE_LABEL,
  manual_csv: "手動CSVコピー",
};

/**
 * コピー完了画面と手動CSV画面で共用する、12結果の固定表示名。
 * 片方だけ言い換えると、どの手動入力がどの保存結果になるか分からなくなる。
 */
export const STOCKY_COPY_RESULT_DOMAIN_LABEL: Record<
  StockyCopyResultDomain,
  string
> = {
  suppliers: "仕入先",
  purchase_tax_types: "仕入用税区分",
  variant_suppliers: "商品と仕入先の紐付け",
  purchase_orders: "発注書・明細",
  stock_adjustments: "在庫調整・明細",
  reorder_policies: "Low Stock・発注点",
  supplier_lead_times: "リードタイム",
  minimum_order_quantities: "最低発注数量（MOQ）",
  stocktakes: "棚卸し履歴・明細",
  product_catalog: "Stockyの商品・バリエーション",
  forecasting_defaults: "需要予測設定",
  report_originals: "Stock on hand・全レポート原本",
};

// Stocky API copy ends with August 31 in Japan. The absolute instant keeps the
// default tab stable even when the merchant's browser uses another timezone.
export const STOCKY_API_COPY_DEADLINE_MS = Date.parse(
  "2026-09-01T00:00:00+09:00",
);

export function isStockyApiCopyProfile(
  profile: StockyCopyProfile,
): boolean {
  return profile.startsWith("api_plus_web_");
}

export function isNewStockyApiCopyAllowed(
  profile: StockyCopyProfile,
  nowMs = Date.now(),
): boolean {
  return !isStockyApiCopyProfile(profile) || nowMs < STOCKY_API_COPY_DEADLINE_MS;
}

export function stockyCopyTabIds(
  nowMs = Date.now(),
): readonly StockyCopyTabId[] {
  return nowMs < STOCKY_API_COPY_DEADLINE_MS
    ? STOCKY_COPY_TAB_IDS
    : ["web_only_v3", "manual_csv"];
}

export function isStockyCopyProfile(
  value: string | null,
): value is StockyCopyProfile {
  return (
    value !== null &&
    (STOCKY_COPY_PROFILES as readonly string[]).includes(value)
  );
}

export function stockyCopyProfileFromSearch(
  search: string,
  nowMs = Date.now(),
): StockyCopyProfile {
  const requested = new URLSearchParams(search).get("profile");
  if (isStockyCopyProfile(requested)) {
    return isNewStockyApiCopyAllowed(requested, nowMs)
      ? requested
      : "web_only_v3";
  }
  return nowMs < STOCKY_API_COPY_DEADLINE_MS
    ? "api_plus_web_v3"
    : "web_only_v3";
}

export function searchForStockyCopyProfile(
  search: string,
  profile: StockyCopyProfile,
): string {
  const params = new URLSearchParams(search);
  params.set("profile", profile);
  const value = params.toString();
  return value ? `?${value}` : "";
}

/** Preserve Shopify entry context while leaving API/web profile selection. */
export function searchForManualStockyCopy(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("profile");
  const value = params.toString();
  return value ? `?${value}` : "";
}
